import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { between, Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import SearchBarStyles from '@stories/Widgets/SearchBar/SearchBar.styles';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled, { createGlobalStyle, css } from 'styled-components';
export const DesktopBreakpoint = 1152;
const GlobalStyles = createGlobalStyle `
  :root {
    --headerHeight: 96px;

    @media ${from(DesktopBreakpoint)} {
      --headerHeight: 112px;
    }
  }
`;
const Container = styled.header `
  --shadow-color: 0deg 0% 63%;
  --dropdownGap: 12px;

  display: flex;
  align-items: center;
  height: var(--headerHeight);
  background-color: ${brand.global.white};
  /* Generated using https://www.joshwcomeau.com/shadow-palette/ */
  box-shadow: 0px 0.1px 0.1px hsl(var(--shadow-color) / 0.36),
    0px 0.5px 0.6px -0.8px hsl(var(--shadow-color) / 0.36),
    0px 1.2px 1.3px -1.7px hsl(var(--shadow-color) / 0.36),
    -0.1px 2.9px 3.3px -2.5px hsl(var(--shadow-color) / 0.36);
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${Layers.Navigation};
  width: 100%;
`;
const Inner = styled.div `
  ${siteWide};

  display: flex;
  height: 100%;
  align-items: center;
`;
const Logo = styled.a `
  ${transition('opacity')};

  display: block;
  height: 48px;
  border-radius: 2px;

  &:focus-visible {
    outline-style: solid;
    outline-offset: 5px;
    outline-width: 2px;
    outline-color: ${brand.primary.blue};
  }

  &:focus-visible,
  &:hover {
    opacity: 0.65;
  }

  svg {
    display: block;
    height: 100%;
  }

  @media ${from(DesktopBreakpoint)} {
    height: 64px;
  }
`;
const trayStyles = css `
  position: fixed;
  top: var(--headerHeight);
  right: 0;
  width: 100%;
  height: calc(100dvh - var(--headerHeight));
  background-color: ${brand.global.white};
  transform: translateX(101vw);
  max-width: 600px;
  overflow: auto;
  overflow-x: clip;
`;
const Nav = styled.nav `
  display: flex;
  align-items: center;
  height: 100%;
  color: ${brand.primary.blue};

  > ul {
    display: contents;
  }

  @media ${until(DesktopBreakpoint)} {
    ${transition('transform, opacity')};
    ${trayStyles};

    flex-direction: column;

    &[aria-expanded='true'],
    &:has(div[aria-current='true']) {
      transform: translateX(0);
    }
  }

  @media ${from(DesktopBreakpoint)} {
    gap: ${fluid(12, 24)};
    margin-left: ${fluid(24, 32)};
    flex: 1;
  }
`;
const NavLink = styled.li `
  display: flex;
  align-items: center;

  @media ${until(DesktopBreakpoint)} {
    width: calc(100% - 24px);
    margin: 12px 12px 0;

    &:has(> button),
    &[data-back] {
      width: 100%;
      background-color: ${brand.grey.light};
      padding: 12px;
      margin: 0;
    }

    &:has(> button) {
      margin-top: auto;
    }

    &:first-child:has(> button):not([data-back]) {
      height: 0;
      padding: 0;
    }
  }

  @media ${from(DesktopBreakpoint)} {
    position: relative;
    height: 100%;

    &:first-child:has(> button) {
      margin-left: auto;
    }

    &[data-back] {
      display: none;
    }
  }
`;
const NavLinkTextContainer = styled.span ``;
const NavLinkAnchor = styled(m.a) `
  ${ButtonReset};

  position: relative;
  display: flex;
  gap: ${fluid(16, 18)};
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  color: currentColor;
  text-decoration: none;

  &:not([href]) {
    cursor: default;
  }

  &:focus-visible {
    outline-style: solid;
    outline-offset: 5px;
    outline-width: 2px;
    outline-color: currentColor;
  }

  svg,
  img {
    width: ${fluid(20, 24)};
    flex: 0 0 auto;
    object-fit: contain;
  }

  svg,
  img,
  ${NavLinkTextContainer} {
    position: relative;
  }

  button&,
  [data-account] & {
    img {
      border-radius: 5000px;
    }
  }

  a& {
    [data-account] & {
      svg:last-child {
        margin-left: auto;
        transform: rotate(-90deg);
      }
    }
  }

  [data-back] & {
    svg:first-child {
      transform: rotate(90deg);
      margin-right: auto;
    }
  }

  @media ${until(DesktopBreakpoint)} {
    width: 100%;

    &[data-search] {
      display: none;
    }

    &[data-icon-only] svg:nth-child(3) {
      transform: rotate(-90deg);
      margin-left: auto;
    }
  }

  @media ${from(DesktopBreakpoint)} {
    align-items: flex-start;

    /* Use for background-colour hover animation due to better performance */
    &::before {
      ${transition('opacity')};

      content: '';
      position: absolute;
      inset: 0;
      height: 100%;
      width: 100%;
      border-radius: 8px;
      border: 1px solid currentColor;
      background-color: ${brand.grey.light};
      opacity: 0;
    }

    &:focus-visible,
    &:hover {
      &::before {
        opacity: 1;
      }
    }

    &[data-top='true'] {
      img,
      svg {
        display: none;
      }
    }

    &[data-icon-only] svg:nth-child(3) {
      display: none;
    }

    &[data-top='true'],
    button& {
      padding: 0;
      gap: 0;

      &::before {
        display: none;
      }
    }

    [data-account] & {
      align-items: center;
      width: 100%;

      svg:last-child {
        ${transition('opacity, transform', 0.65)};

        width: 12px;
        transform: rotate(-90deg) translateY(-50%);
        opacity: 0;
      }

      &:focus-visible,
      &:hover {
        svg:last-child {
          opacity: 1;
          transform: rotate(-90deg) translateY(0);
        }
      }
    }

    [data-back] & {
      display: none;
    }
  }
`;
const NavLinkText = styled.span `
  ${fonts.dmSans.bold};

  font-size: 16px;
  line-height: 1.25;

  @media ${from(DesktopBreakpoint)} {
    font-size: 14px;

    ${NavLinkAnchor}[data-icon-only] &,
    ${NavLinkAnchor}[data-top] & {
      font-weight: 500;
    }

    ${NavLinkAnchor}[data-icon-only] & {
      ${srOnly};
    }
  }
`;
const NavLinkDescription = styled.span `
  ${fonts.dmSans.regular};

  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;

  @media ${until(DesktopBreakpoint)} {
    display: none;
  }
`;
const NavInitials = styled.span `
  ${fonts.dmSans.bold};

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${fluid(20, 24)};
  height: ${fluid(20, 24)};
  font-size: 12px;
  line-height: 1.25;
  border-radius: 5000px;
  background-color: ${brand.primary.blue};
  color: ${brand.global.white};
`;
const Underline = styled(m.span) `
  position: absolute;
  bottom: -6px;
  left: 0;
  height: 2px;
  width: 100%;
  border-radius: 100px;
  background-color: currentColor;

  @media ${until(DesktopBreakpoint)} {
    display: none;
  }
`;
const DropdownLinks = styled.ul `
  ${transition('opacity, transform', 1)};

  opacity: 0;
  pointer-events: none;

  &[aria-current='true'] {
    opacity: 1;
    pointer-events: auto;
  }

  ${SearchBarStyles.InputWrapper} {
    background-color: ${brand.grey.light};
  }

  @media ${until(DesktopBreakpoint)} {
    ${trayStyles};

    z-index: 1;
    top: 0;
    padding: 0 0 12px;
    transition-duration: 650ms;

    &[data-search],
    &[aria-current='true'] {
      transform: translateX(0);
    }

    &[data-search] {
      padding: 24px 20px;
    }

    > li {
      margin: 12px 12px 0;
    }

    ${SearchBarStyles.FormWrapper} {
      height: 100%;

      ${ButtonStyles.Container} {
        --buttonHeight: 58px;

        position: absolute;
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        text-align: center;
        justify-content: center;
      }
    }

    ${SearchBarStyles.Button}[data-type='clear'] {
      grid-column: 3;
    }

    ${SearchBarStyles.Suggestions} {
      top: 82px;
    }
  }

  @media ${from(DesktopBreakpoint)} {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    padding: calc(var(--headerHeight) + var(--dropdownGap) + 24px) 24px 12px;
    width: 324px;
    z-index: -1;

    // Background colour for the dropdown
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: calc(100% - var(--headerHeight) - var(--dropdownGap));
      background-color: ${brand.global.white};
      border-radius: 12px;
    }

    &:has(> li:nth-child(5)) {
      width: 600px;
      columns: 2;

      @media ${between(DesktopBreakpoint, Device.DesktopLarge)} {
        &[data-first='true'] {
          transform: translateX(-25%);
        }
      }
    }

    > li {
      padding-bottom: 12px;
    }

    &[data-account] {
      width: 240px;
    }

    &[data-search],
    &[data-account] {
      left: auto;
      right: -24px;
      transform: none;
    }

    &[data-search] {
      padding: calc(var(--headerHeight) + var(--dropdownGap) + 16px) 16px 16px;
      width: 600px;
    }
  }
`;
const Background = styled.div `
  ${transition('opacity')};

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${rgba(brand.grey.dark, 0.5)};
  backdrop-filter: blur(4px);
  pointer-events: none;
  opacity: 0;
  z-index: ${Layers.Navigation - 1};

  &[data-active='true'] {
    opacity: 1;
  }
`;
const MenuButton = styled.button `
  ${ButtonReset};

  display: grid;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  color: ${brand.primary.blue};
  font-size: 16px;
  line-height: 1.25;

  &:focus-visible {
    outline-style: solid;
    outline-offset: 5px;
    outline-width: 2px;
    outline-color: currentColor;
  }

  svg {
    ${transition('opacity, transform')};

    grid-area: 1 / 1 / 1 / 1;
    width: 100%;
    height: 100%;

    &:nth-child(2) {
      opacity: 0;
      transform: scale(0.5);
    }
  }

  &[aria-expanded='true'] {
    svg:nth-child(1) {
      opacity: 0;
      transform: scale(0.5);
    }

    svg:nth-child(2) {
      opacity: 1;
      transform: scale(1);
    }
  }

  &:first-of-type {
    margin: 0 16px 0 auto;
  }

  @media ${from(DesktopBreakpoint)} {
    display: none;
  }
`;
const HeaderStyles = {
    Background,
    Container,
    DropdownLinks,
    GlobalStyles,
    Inner,
    Logo,
    MenuButton,
    Nav,
    NavInitials,
    NavLink,
    NavLinkAnchor,
    NavLinkDescription,
    NavLinkText,
    NavLinkTextContainer,
    Underline,
};
export default HeaderStyles;
